
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { LoadingLogoComponent } from "./loadinglogo.component";
import { CommonModule } from '@angular/common';

type Mode =
    | 'float'
    | 'ingress'
    | 'egress'
    | 'eye'
    | 'orbit'
    | 'streamVertical'
    | 'streamHorizontal'
    | 'pulse'
    | 'scan'
    | 'glitch';

type Particle = {
    id: number;
    text: string;
    bx: number;
    by: number;
    x: number;
    y: number;
    size: number;
    opacity: number;
    rot: number;
    dx: number;
    dy: number;
    inx: number;
    iny: number;
    odx: number;
    ody: number;
    orx: number;
    ory: number;
    orbitTheta: number;
    orbitSpeed: number;
    dur: number;
    delay: number;
};

@Component({
    selector: 'app-loading',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './loading.component.html',
    styleUrl: './loading.component.css',
    imports: [CommonModule, LoadingLogoComponent]
})
export class LoadingComponent implements OnChanges, OnDestroy {
    constructor(
        private readonly cdr: ChangeDetectorRef,
    ) { }

    @Input() isVisible = false;

    mode: Mode = 'orbit';
    orb = { cx: 525.58, cy: 317.44, r: 172 };
    eye = { fx: this.orb.cx, fy: this.orb.cy, t: 0 };
    density = 64;
    speed = 1;
    intensity = 1;
    sizeScale = 1;
    particles: Particle[] = [];
    showEyelids: boolean = true;
    showBrackets: boolean = true;
    private raf = 0;
    private lastTs = 0;

    private rand(min: number, max: number) { return min + Math.random() * (max - min); }
    private randInt(min: number, max: number) { return Math.floor(this.rand(min, max + 1)); }
    private round2(n: number) { return Math.round(n * 100) / 100; }
    private round3(n: number) { return Math.round(n * 1000) / 1000; }

    particleVars = (p: Particle): string => this.particleStyle(p);

    private particleStyle(p: Particle): string {
        return [
            `--dx:${p.dx}px`,
            `--dy:${p.dy}px`,
            `--inx:${p.inx}px`,
            `--iny:${p.iny}px`,
            `--odx:${p.odx}px`,
            `--ody:${p.ody}px`,
            `--dur:${p.dur}s`,
            `--delay:${p.delay}s`,
            `--op:${p.opacity}`,
            `--rot:${p.rot}deg`,
            `--sx:${this.round2(p.x - this.orb.cx)}px`,
            `--sy:${this.round2(p.y - this.orb.cy)}px`,
            `--outx:${this.round2(-p.inx)}px`,
            `--outy:${this.round2(-p.iny)}px`,
            `--orbR:${this.orb.r}px`,
        ].join(';');
    }

    private readonly rawSymbols = [
        '<>', '</>', '{}', '{{}}', '[]', '[[]]', '()', '(())',
        '=>', '==', '===', '!=', '!==', '<=', '>=',
        '&&', '||', '++', '--',
        '::', '->', '>>>', '<<', '>>',
        '><', '<=>', '|>', '<|', '??', '?:',
        '<!-- -->', '</', '/>',
    ];

    cssVarsForScene(): Record<string, string> {
        return {
            '--speed': String(this.speed),
            '--intensity': String(this.intensity),
            '--scale': String(this.sizeScale),
            '--mode': this.mode,
        };
    }

    ngOnChanges(): void {
        if (this.isVisible) {
            this.show();
            return;
        }

        this.stopEyeLoop();
    }

    ngOnDestroy(): void {
        this.stopEyeLoop();
    }

    regenerate(): void {
        this.particles = this.makeParticles(this.density);
    }

    private startEyeLoop(): void {
        if (this.raf) return;

        const tick = (now: number) => {
            if (!this.isVisible) {
                this.raf = 0;
                return;
            }

            if (!this.lastTs) {
                this.lastTs = now;
            }
            const dt = Math.min(0.05, (now - this.lastTs) / 1000);
            this.lastTs = now;

            this.eye.t += 0.016 * this.speed;
            const amp = 34 * this.intensity;
            const fx = (this.mode === 'pulse' || this.mode === 'ingress' || this.mode === 'egress')
                ? this.orb.cx
                : this.orb.cx + Math.cos(this.eye.t * 0.9) * amp;
            const fy = (this.mode === 'scan' || this.mode === 'pulse' || this.mode === 'ingress' || this.mode === 'egress')
                ? this.orb.cy
                : this.orb.cy + Math.sin(this.eye.t * 1.2) * amp * 0.75;

            this.eye.fx = fx;
            this.eye.fy = fy;

            if (this.mode === 'orbit') {
                this.stepOrbit(dt);
            }

            this.cdr.markForCheck();

            this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
    }

    private show(): void {
        if (!this.particles.length) {
            this.regenerate();
        }

        this.startEyeLoop();
    }

    private stopEyeLoop(): void {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
        this.lastTs = 0;
    }

    private stepOrbit(dt: number): void {
        const k = this.speed * (0.8 + this.intensity * 0.45);
        for (const p of this.particles) {
            p.orbitTheta += p.orbitSpeed * k * dt;
            const c = Math.cos(p.orbitTheta);
            const s = Math.sin(p.orbitTheta);
            const nx = p.orx * c - p.ory * s;
            const ny = p.orx * s + p.ory * c;
            p.x = this.round2(this.orb.cx + nx);
            p.y = this.round2(this.orb.cy + ny);
        }
    }

    private randomPointInCircle(cx: number, cy: number, radius: number) {
        const t = Math.random() * Math.PI * 2;
        const rr = Math.sqrt(Math.random()) * radius;
        return { x: cx + rr * Math.cos(t), y: cy + rr * Math.sin(t) };
    }

    private makeParticles(count: number): Particle[] {
        const res: Particle[] = [];
        for (let i = 0; i < count; i++) {
            const pt = this.randomPointInCircle(this.orb.cx, this.orb.cy, this.orb.r * 0.92);
            const dx = this.rand(-12, 12);
            const dy = this.rand(-12, 12);
            const toCx = this.orb.cx - pt.x;
            const toCy = this.orb.cy - pt.y;
            const inScale = this.rand(0.92, 1.08);
            const inx = toCx * inScale;
            const iny = toCy * inScale;
            const odx = (dy * 0.9);
            const ody = (-dx * 0.9);
            const orx = pt.x - this.orb.cx;
            const ory = pt.y - this.orb.cy;
            const orbitSpeed = this.rand(0.45, 1.2) * (Math.random() < 0.5 ? -1 : 1);

            res.push({
                id: i,
                text: this.rawSymbols[Math.floor(Math.random() * this.rawSymbols.length)],
                bx: this.round2(pt.x),
                by: this.round2(pt.y),
                x: this.round2(pt.x),
                y: this.round2(pt.y),
                size: this.randInt(12, 22),
                opacity: this.round3(this.rand(0.24, 0.44)),
                rot: this.randInt(-35, 35),
                dx: this.round2(dx),
                dy: this.round2(dy),
                inx: this.round2(inx),
                iny: this.round2(iny),
                odx: this.round2(odx),
                ody: this.round2(ody),
                orx: this.round2(orx),
                ory: this.round2(ory),
                orbitTheta: 0,
                orbitSpeed: this.round3(orbitSpeed),
                dur: this.round2(this.rand(2.8, 6.5)),
                delay: this.round2(this.rand(-2.2, 0.0)),
            });
        }
        return res;
    }
}
